// src/components/formularios/FormularioRespuestaDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Rating,
  Typography,
  Box,
  LinearProgress,
  Stack,
  Alert,
  Autocomplete,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';
import {
  submitRespuestas,
  validarRespuestas,
  getAlumnosProfesor,
  getUsuariosDepartamento,
  getUsuariosObjetivoPredefinidos,
  getUsuariosObjetivoCompletados,
  guardarBorrador,
  obtenerBorrador,
  getFormulario,
  getPreguntasEvaluacion,
  type FormularioPendiente,
  type FormularioPregunta,
  type RespuestaItemDto,
} from '../../api/formularios';
import { useAuth } from '../../context/AuthContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmExitFormularioDialog from './ConfirmExitFormularioDialog';
import ConfirmExitEvaluacionDialog from './ConfirmExitEvaluacionDialog';

interface Usuario {
  id: number;
  nombreCompleto?: string;
  nombre_completo?: string; // Campo que viene del backend
  email?: string;
}

interface Props {
  open: boolean;
  formulario: FormularioPendiente & { tipoFormulario?: string; respuestasAnonimas?: boolean };
  onClose: () => void;
  onComplete: () => void;
  onRespuestasChange?: (nuevasRespuestas: RespuestaItemDto[]) => void;
  respuestasIniciales?: RespuestaItemDto[];
  deshabilitarSalida?: boolean;
  mostrarAdvertenciaTiempo?: boolean;
  autoCloseOnResult?: boolean;
  autoSubmitOnCancel?: boolean;
}

export default function FormularioRespuestaDialog({
  open,
  formulario,
  onClose,
  onComplete,
  onRespuestasChange,
  respuestasIniciales,
  deshabilitarSalida,
  mostrarAdvertenciaTiempo,
  autoCloseOnResult,
  autoSubmitOnCancel,
}: Props) {
  const { user } = useAuth();
  const [usuariosObjetivo, setUsuariosObjetivo] = useState<Usuario[]>([]);
  const [usuariosSeleccionados, setUsuariosSeleccionados] = useState<number[]>([]);
  const [respuestasPorUsuario, setRespuestasPorUsuario] = useState<
    Record<number, Record<number, any>>
  >({});
  const [visibleQuestionsByUser, setVisibleQuestionsByUser] = useState<
    Record<number, Set<number>>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmAutoSubmit, setShowConfirmAutoSubmit] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [usuariosCompletados, setUsuariosCompletados] = useState<Set<number>>(new Set());
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState<Record<number, boolean>>({});
  const [resultadoEvaluacion, setResultadoEvaluacion] = useState<{
    calificacion: number;
    aprobado: boolean;
    competenciasFalladas?: string[];
  } | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const debounceMs = deshabilitarSalida ? 5000 : 1000;
  const [debouncedRespuestasPorUsuario] = useDebounce(respuestasPorUsuario, debounceMs);

  // Nuevos estados para manejar el diálogo de confirmación
  const [showConfirmExit, setShowConfirmExit] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  // Estado para el formulario completo con preguntas y opciones
  const [formularioCompleto, setFormularioCompleto] = useState<any>(null);
  const [preguntasEvaluacion, setPreguntasEvaluacion] = useState<
    { preguntas: FormularioPregunta[] } | null
  >(null);
  const [loadingFormulario, setLoadingFormulario] = useState(true);
  const [errorCargarFormulario, setErrorCargarFormulario] = useState<string | null>(null);

  // Cargar formulario completo con preguntas y opciones
  useEffect(() => {
    if (open && formulario?.id) {
      setLoadingFormulario(true);
      setErrorCargarFormulario(null);
      getFormulario(formulario.id)
        .then((form) => {
          if (form.preguntas) {
            form.preguntas.forEach((p: FormularioPregunta) => {
              if (
                (p.tipo === 'seleccion_unica' || p.tipo === 'seleccion_multiple') &&
                (!p.opciones || p.opciones.length === 0)
              ) {
                console.warn(`Pregunta ${p.id} (${p.tipo}) no tiene opciones cargadas`);
              }
            });
          }
          setFormularioCompleto(form);
          setErrorCargarFormulario(null);
        })
        .catch((error) => {
          console.error('Error al cargar formulario completo:', error);
          setErrorCargarFormulario(
            'Error al cargar el formulario completo. Por favor, intenta recargar la página.',
          );
          enqueueSnackbar('Error al cargar el formulario completo', { variant: 'error' });
        })
        .finally(() => setLoadingFormulario(false));
    }
  }, [open, formulario?.id, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return;
    if (
      formularioCompleto?.esEvaluacion &&
      typeof formularioCompleto?.numeroPreguntasMostrar === 'number' &&
      formularioCompleto.numeroPreguntasMostrar > 0
    ) {
      getPreguntasEvaluacion(formulario.id)
        .then((data) => {
          setPreguntasEvaluacion({ preguntas: data.preguntas });
        })
        .catch(() => {
          setPreguntasEvaluacion(null);
        });
    } else {
      setPreguntasEvaluacion(null);
    }
  }, [open, formulario.id, formularioCompleto?.esEvaluacion, formularioCompleto?.numeroPreguntasMostrar]);

  useEffect(() => {
    if (open) {
      loadUsuariosObjetivo();
      loadUsuariosCompletados();
      setErrors({});
      setDraftSaved({});
      setResultadoEvaluacion(null);

      // Inicializar respuestas desde `respuestasIniciales` si vienen (modo evaluación con tiempo)
      if (
        respuestasIniciales &&
        Array.isArray(respuestasIniciales) &&
        respuestasIniciales.length > 0
      ) {
        const inicial: Record<number, Record<number, any>> = {};
        // Usar usuario objetivo 0 (formulario general / single)
        inicial[0] = {};
        respuestasIniciales.forEach((r) => {
          inicial[0][r.preguntaId] = {
            respuestaTexto: r.respuestaTexto,
            respuestaNumero: r.respuestaNumero,
            opcionId: r.opcionId,
            opcionIds: r.opcionIds || r.opcionesIds,
          };
        });
        setRespuestasPorUsuario(inicial);
      }
    }
  }, [open, formulario, formularioCompleto]);

  const handleCerrarResultado = async () => {
    setResultadoEvaluacion(null);
    try {
      await loadUsuariosCompletados();
    } catch {
      // best-effort
    }
    onComplete();
    onClose();
      window.dispatchEvent(
        new CustomEvent('formulario:completed', {
          detail: { formularioId: formulario.id },
        }),
      );
  };

  // Sincronizar cambios de respuestas hacia el padre (debounced)
  useEffect(() => {
    if (!onRespuestasChange) return;
    const flattened: RespuestaItemDto[] = [];
    Object.entries(debouncedRespuestasPorUsuario || {}).forEach(
      ([usuarioIdStr, respuestasUsuario]) => {
        const usuarioObjetivoId = usuarioIdStr === '0' ? undefined : Number(usuarioIdStr);
        Object.entries(respuestasUsuario || {}).forEach(([preguntaIdStr, answer]) => {
          const preguntaId = Number(preguntaIdStr);
          const item: RespuestaItemDto = {
            preguntaId,
            respuestaTexto: answer?.respuestaTexto,
            respuestaNumero: answer?.respuestaNumero,
            opcionId: answer?.opcionId,
            opcionIds: answer?.opcionIds || answer?.opcionesIds,
            usuarioObjetivoId,
          };
          flattened.push(item);
        });
      },
    );

    onRespuestasChange(flattened);
  }, [debouncedRespuestasPorUsuario, onRespuestasChange]);

  // Cargar borrador para un usuario objetivo específico
  const loadBorrador = async (usuarioObjetivoId?: number) => {
    if (!formulario.id) return;
    try {
      const borrador = await obtenerBorrador(formulario.id, usuarioObjetivoId);
      if (borrador && borrador.respuestasJson && borrador.respuestasJson.respuestas) {
        const respuestasRestauradas: Record<number, any> = {};
        borrador.respuestasJson.respuestas.forEach((r: any) => {
          respuestasRestauradas[r.preguntaId] = {
            respuestaTexto: r.respuestaTexto,
            respuestaNumero: r.respuestaNumero,
            opcionId: r.opcionId,
            opcionIds: r.opcionIds || r.opcionesIds,
          };
        });

        if (usuarioObjetivoId) {
          setRespuestasPorUsuario((prev) => ({
            ...prev,
            [usuarioObjetivoId]: respuestasRestauradas,
          }));
          setDraftSaved((prev) => ({ ...prev, [usuarioObjetivoId]: true }));
        } else {
          setRespuestasPorUsuario({ 0: respuestasRestauradas });
          setDraftSaved({ 0: true });
        }
      }
    } catch (error) {
      // Si no hay borrador, continuar normalmente
    }
  };

  // Función auxiliar para guardar borrador de un usuario
  const guardarBorradoresForUser = async (usuarioObjetivoId: number) => {
    if (!formulario.id) return;

    const respuestasUsuario = respuestasPorUsuario[usuarioObjetivoId] || {};
    if (Object.keys(respuestasUsuario).length === 0) return;

    const respuestasArray = Object.entries(respuestasUsuario).map(([preguntaId, answer]) => ({
      preguntaId: Number(preguntaId),
      ...answer,
    }));

    await guardarBorrador(formulario.id, {
      respuestas: respuestasArray,
      usuarioObjetivoId: usuarioObjetivoId === 0 ? undefined : usuarioObjetivoId,
    });
  };

  // Guardar borrador automáticamente con debounce por usuario objetivo
  useEffect(() => {
    if (!open || !formulario.id || Object.keys(debouncedRespuestasPorUsuario).length === 0) return;

    const guardarBorradoresAuto = async () => {
      for (const [usuarioObjetivoIdStr, respuestasUsuario] of Object.entries(
        debouncedRespuestasPorUsuario,
      )) {
        const usuarioObjetivoId =
          usuarioObjetivoIdStr === '0' ? undefined : Number(usuarioObjetivoIdStr);

        if (Object.keys(respuestasUsuario).length === 0) continue;

        try {
          setSavingDraft(true);
          const respuestasArray = Object.entries(respuestasUsuario).map(([preguntaId, answer]) => ({
            preguntaId: Number(preguntaId),
            ...answer,
          }));

          await guardarBorrador(formulario.id, {
            respuestas: respuestasArray,
            usuarioObjetivoId: usuarioObjetivoId,
          });

          setDraftSaved((prev) => ({
            ...prev,
            [usuarioObjetivoId || 0]: true,
          }));
        } catch (error: any) {
          console.error(`Error guardando borrador:`, error);
        } finally {
          setSavingDraft(false);
        }
      }
    };

    guardarBorradoresAuto();

    // Limpiar el indicador de borrador guardado después de 3 segundos
    const timer = setTimeout(() => {
      setDraftSaved({});
    }, 3000);

    return () => clearTimeout(timer);
  }, [debouncedRespuestasPorUsuario, open, formulario.id]);

  // Manejador para cerrar el diálogo con confirmación
  const handleClose = () => {
    if (resultadoEvaluacion) {
      handleCerrarResultado();
      return;
    }

    if (autoSubmitOnCancel) {
      setShowConfirmAutoSubmit(true);
      return;
    }
    // Verificar si hay respuestas sin enviar
    const hasUnsavedChanges = Object.keys(respuestasPorUsuario).some(
      (usuarioId) => Object.keys(respuestasPorUsuario[Number(usuarioId)] || {}).length > 0,
    );

    if (hasUnsavedChanges && !draftSaved[Number(Object.keys(respuestasPorUsuario)[0]) || 0]) {
      setShowConfirmExit(true);
      setPendingClose(true);
    } else {
      onClose();
    }
  };

  const buildRespuestas = (): RespuestaItemDto[] => {
    const respuestas: RespuestaItemDto[] = [];

    for (const usuarioObjetivoId of usuariosSeleccionados) {
      const visibles = visibleQuestionsByUser[usuarioObjetivoId] || new Set<number>();
      const visiblePreguntas = preguntasConOpciones.filter((q: FormularioPregunta) =>
        visibles.has(q.id),
      );
      for (const pregunta of visiblePreguntas) {
        const respuestasUsuario = respuestasPorUsuario[usuarioObjetivoId] || {};
        const answer = respuestasUsuario[pregunta.id];

        const base: RespuestaItemDto = {
          preguntaId: pregunta.id,
          usuarioObjetivoId:
            formulario.tipoFormulario !== 'general' ? usuarioObjetivoId : undefined,
        };

        if (!answer) {
          if (pregunta.tipo === 'fecha' || pregunta.tipo === 'hora') {
            respuestas.push(base);
          }
          continue;
        }

        if (pregunta.tipo === 'puntuacion') {
          if (
            answer.respuestaNumero !== undefined &&
            answer.respuestaNumero !== null &&
            answer.respuestaNumero !== 0
          ) {
            base.respuestaNumero = answer.respuestaNumero;
            respuestas.push(base);
          }
        } else if (pregunta.tipo === 'texto_corto' || pregunta.tipo === 'texto_largo') {
          if (answer.respuestaTexto && answer.respuestaTexto.trim() !== '') {
            base.respuestaTexto = answer.respuestaTexto;
            respuestas.push(base);
          }
        } else if (
          pregunta.tipo === 'seleccion_unica' ||
          pregunta.tipo === 'test_seleccion_unica'
        ) {
          if (answer.opcionId) {
            base.opcionId = answer.opcionId;
            respuestas.push(base);
          }
        } else if (
          pregunta.tipo === 'seleccion_multiple' ||
          pregunta.tipo === 'test_seleccion_multiple'
        ) {
          if (answer.opcionIds && Array.isArray(answer.opcionIds) && answer.opcionIds.length > 0) {
            base.opcionIds = answer.opcionIds;
            respuestas.push(base);
          }
        } else if (pregunta.tipo === 'fecha' || pregunta.tipo === 'hora') {
          respuestas.push(base);
        }
      }
    }

    if (formulario.tipoFormulario === 'general' || !formulario.tipoFormulario) {
      const visibles = visibleQuestionsByUser[0] || new Set<number>();
      const visiblePreguntas = preguntasConOpciones.filter((q: FormularioPregunta) =>
        visibles.has(q.id),
      );
      for (const pregunta of visiblePreguntas) {
        const answer = respuestasPorUsuario[0]?.[pregunta.id];
        const base: RespuestaItemDto = { preguntaId: pregunta.id };

        if (!answer) {
          if (pregunta.tipo === 'fecha' || pregunta.tipo === 'hora') {
            respuestas.push(base);
          }
          continue;
        }

        if (pregunta.tipo === 'puntuacion' && answer.respuestaNumero) {
          base.respuestaNumero = answer.respuestaNumero;
          respuestas.push(base);
        } else if (
          (pregunta.tipo === 'texto_corto' || pregunta.tipo === 'texto_largo') &&
          answer.respuestaTexto
        ) {
          base.respuestaTexto = answer.respuestaTexto;
          respuestas.push(base);
        } else if (
          (pregunta.tipo === 'seleccion_unica' || pregunta.tipo === 'test_seleccion_unica') &&
          answer.opcionId
        ) {
          base.opcionId = answer.opcionId;
          respuestas.push(base);
        } else if (
          (pregunta.tipo === 'seleccion_multiple' ||
            pregunta.tipo === 'test_seleccion_multiple') &&
          answer.opcionIds
        ) {
          base.opcionIds = answer.opcionIds;
          respuestas.push(base);
        } else if (pregunta.tipo === 'fecha' || pregunta.tipo === 'hora') {
          respuestas.push(base);
        }
      }
    }

    return respuestas;
  };

  const handleResult = async (result: any) => {
    setDraftSaved({});
    if (result && typeof result === 'object' && result.calificacion !== undefined) {
      const calificacion = Number(result.calificacion);
      const aprobado = Boolean(result.aprobado);
      const competenciasFalladas = result.feedback?.competenciasFalladas || [];

      if (autoCloseOnResult) {
        const detalleCompetencias = competenciasFalladas.length
          ? ` Áreas: ${competenciasFalladas.join(', ')}.`
          : '';
        enqueueSnackbar(
          `Evaluación enviada. Nota: ${calificacion} (${aprobado ? 'Aprobado' : 'No aprobado'}).${detalleCompetencias}`,
          { variant: aprobado ? 'success' : 'warning' },
        );
        await loadUsuariosCompletados();
        onComplete();
        onClose();
          window.dispatchEvent(
            new CustomEvent('formulario:completed', {
              detail: { formularioId: formulario.id },
            }),
          );
        return;
      }

      setResultadoEvaluacion({
        calificacion,
        aprobado,
        competenciasFalladas,
      });
      return;
    }

    enqueueSnackbar('Formulario completado correctamente', { variant: 'success' });
    await loadUsuariosCompletados();
    onComplete();
    onClose();
      window.dispatchEvent(
        new CustomEvent('formulario:completed', {
          detail: { formularioId: formulario.id },
        }),
      );
  };

  // Guardar borrador y cerrar
  const handleSaveDraftAndClose = async () => {
    try {
      // Guardar borradores para todos los usuarios
      if (formulario.tipoFormulario && formulario.tipoFormulario !== 'general') {
        for (const usuarioId of usuariosSeleccionados) {
          await guardarBorradoresForUser(usuarioId);
        }
      } else {
        await guardarBorradoresForUser(0);
      }

      enqueueSnackbar('Borrador guardado', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('Error al guardar borrador', { variant: 'error' });
    } finally {
      setShowConfirmExit(false);
      setPendingClose(false);
    }
  };

  // Descartar cambios y cerrar
  const handleDiscardAndClose = () => {
    setShowConfirmExit(false);
    setPendingClose(false);
    onClose();
  };

  const loadUsuariosCompletados = async () => {
    if (!formulario.tipoFormulario || formulario.tipoFormulario === 'general') {
      return;
    }

    try {
      const completados = await getUsuariosObjetivoCompletados(formulario.id);
      setUsuariosCompletados(new Set(completados));
    } catch (error: any) {
      // No mostrar error al usuario, es solo informativo
    }
  };

  const loadUsuariosObjetivo = async () => {
    if (!formulario.tipoFormulario || formulario.tipoFormulario === 'general') {
      setUsuariosObjetivo([]);
      setUsuariosSeleccionados([]);
      await loadBorrador(0);
      return;
    }

    setLoadingUsuarios(true);
    try {
      let usuarios: Usuario[] = [];

      if (formulario.tipoFormulario === 'profesor_alumnos') {
        usuarios = await getAlumnosProfesor(formulario.id);
      } else if (formulario.tipoFormulario === 'manager_departamento') {
        usuarios = await getUsuariosDepartamento(formulario.id);
      } else if (formulario.tipoFormulario === 'evaluacion_managers') {
        usuarios = await getUsuariosObjetivoPredefinidos(formulario.id);
        if (usuarios.length > 0) {
          loadBorrador(usuarios[0].id);
        }
      }

      const usuariosMapeados = usuarios.map((u: any) => ({
        ...u,
        nombreCompleto: u.nombreCompleto || u.nombre_completo || `Usuario ${u.id}`,
      }));

      setUsuariosObjetivo(usuariosMapeados);

      if (usuariosMapeados.length > 0) {
        setUsuariosSeleccionados(usuariosMapeados.map((u) => u.id));
        const respuestasIniciales: Record<number, Record<number, any>> = {};
        usuariosMapeados.forEach((u) => {
          respuestasIniciales[u.id] = {};
        });
        setRespuestasPorUsuario(respuestasIniciales);

        usuariosMapeados.forEach((u) => {
          loadBorrador(u.id);
        });
      }

      if (formulario.tipoFormulario === 'manager_departamento' && usuariosMapeados.length === 0) {
        enqueueSnackbar('No hay usuarios disponibles en tu departamento para evaluar', {
          variant: 'info',
        });
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al cargar usuarios objetivo', {
        variant: 'error',
      });
      setUsuariosObjetivo([]);
      setUsuariosSeleccionados([]);
    } finally {
      setLoadingUsuarios(false);
    }
  };

  // Usar preguntas del formulario completo (con opciones) si está disponible
  const preguntasConOpciones: FormularioPregunta[] =
    preguntasEvaluacion?.preguntas || formularioCompleto?.preguntas || formulario.preguntas;

  // Evaluate conditions to show/hide questions
  useEffect(() => {
    const next: Record<number, Set<number>> = {};
    const usuarios =
      formulario.tipoFormulario && formulario.tipoFormulario !== 'general'
        ? usuariosSeleccionados
        : [0];

    usuarios.forEach((usuarioId) => {
      const visible = new Set<number>();
      preguntasConOpciones.forEach((q: FormularioPregunta) => {
        if (!q.condicionesJson) {
          visible.add(q.id);
          return;
        }
        const conditions = q.condicionesJson;
        const respuestasUsuario = respuestasPorUsuario[usuarioId] || {};
        if (conditions.operator === 'AND') {
          const allMet = conditions.rules.every((rule: any) => {
            const answer = respuestasUsuario[rule.preguntaId];
            if (rule.operator === 'equals') {
              return (
                answer?.opcionId === rule.value ||
                answer?.respuestaNumero === rule.value ||
                (Array.isArray(answer?.opcionIds) &&
                  answer?.opcionIds?.includes(rule.value))
              );
            }
            return false;
          });
          if (allMet) visible.add(q.id);
        } else if (conditions.operator === 'OR') {
          const anyMet = conditions.rules.some((rule: any) => {
            const answer = respuestasUsuario[rule.preguntaId];
            if (rule.operator === 'equals') {
              return (
                answer?.opcionId === rule.value ||
                answer?.respuestaNumero === rule.value ||
                (Array.isArray(answer?.opcionIds) &&
                  answer?.opcionIds?.includes(rule.value))
              );
            }
            return false;
          });
          if (anyMet) visible.add(q.id);
        } else {
          visible.add(q.id);
        }
      });
      next[usuarioId] = visible;
    });

    setVisibleQuestionsByUser(next);
  }, [respuestasPorUsuario, preguntasConOpciones, usuariosSeleccionados, formulario.tipoFormulario]);

  const handleAnswer = (usuarioObjetivoId: number, preguntaId: number, value: any) => {
    setRespuestasPorUsuario((prev) => ({
      ...prev,
      [usuarioObjetivoId]: {
        ...(prev[usuarioObjetivoId] || {}),
        [preguntaId]: value,
      },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${usuarioObjetivoId}-${preguntaId}`];
      return next;
    });
  };

  const validate = async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};

    if (formularioCompleto?.esEvaluacion) {
      return true;
    }

    if (formulario.tipoFormulario && formulario.tipoFormulario !== 'general') {
      if (usuariosSeleccionados.length === 0) {
        enqueueSnackbar('Debes seleccionar al menos un usuario objetivo', { variant: 'warning' });
        return false;
      }
    }

    const usuariosValidar =
      formulario.tipoFormulario && formulario.tipoFormulario !== 'general'
        ? usuariosSeleccionados
        : [0];

    for (const usuarioObjetivoId of usuariosValidar) {
      const visibles = visibleQuestionsByUser[usuarioObjetivoId] || new Set<number>();
      const visiblePreguntas = preguntasConOpciones.filter((q: FormularioPregunta) =>
        visibles.has(q.id),
      );
      for (const pregunta of visiblePreguntas) {
        if (pregunta.requerida) {
          const respuestasUsuario = respuestasPorUsuario[usuarioObjetivoId] || {};
          const answer = respuestasUsuario[pregunta.id];
          const isMultiple =
            pregunta.tipo === 'seleccion_multiple' || pregunta.tipo === 'test_seleccion_multiple';
          const isSingle =
            pregunta.tipo === 'seleccion_unica' || pregunta.tipo === 'test_seleccion_unica';
          if (
            !answer ||
            (isMultiple && (!answer.opcionIds || answer.opcionIds.length === 0)) ||
            (isSingle && !answer.opcionId)
          ) {
            newErrors[`${usuarioObjetivoId}-${pregunta.id}`] = 'Esta pregunta es obligatoria';
          }
        }
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!(await validate())) return;

    setSubmitting(true);
    try {
      const respuestas = buildRespuestas();
      const result = await submitRespuestas(formulario.id, { respuestas });
      await handleResult(result);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al enviar respuestas', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPartial = async () => {
    setSubmitting(true);
    try {
      const respuestas = buildRespuestas();
      const result = await submitRespuestas(formulario.id, { respuestas });
      await handleResult(result);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al enviar respuestas', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
      setShowConfirmAutoSubmit(false);
    }
  };

  const renderQuestion = (pregunta: FormularioPregunta, usuarioObjetivoId: number) => {
    const visibles = visibleQuestionsByUser[usuarioObjetivoId] || new Set<number>();
    if (!visibles.has(pregunta.id)) return null;

    const respuestasUsuario = respuestasPorUsuario[usuarioObjetivoId] || {};
    const answer = respuestasUsuario[pregunta.id] || {};
    const errorKey = `${usuarioObjetivoId}-${pregunta.id}`;
    const error = errors[errorKey];

    return (
      <Box key={`${usuarioObjetivoId}-${pregunta.id}`} sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {pregunta.pregunta}
          {pregunta.requerida && <span style={{ color: 'red' }}> *</span>}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {pregunta.tipo === 'puntuacion' && (
          <Rating
            value={answer.respuestaNumero || 0}
            max={pregunta.escalaMax || 5}
            onChange={(_, value) =>
              handleAnswer(usuarioObjetivoId, pregunta.id, { respuestaNumero: value || 0 })
            }
            size="large"
          />
        )}

        {pregunta.tipo === 'texto_corto' && (
          <TextField
            fullWidth
            value={answer.respuestaTexto || ''}
            onChange={(e) =>
              handleAnswer(usuarioObjetivoId, pregunta.id, { respuestaTexto: e.target.value })
            }
            error={!!error}
            inputProps={{ maxLength: pregunta.limiteCaracteres }}
            helperText={
              pregunta.limiteCaracteres
                ? `${(answer.respuestaTexto || '').length}/${pregunta.limiteCaracteres}`
                : ''
            }
          />
        )}

        {pregunta.tipo === 'texto_largo' && (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={answer.respuestaTexto || ''}
            onChange={(e) =>
              handleAnswer(usuarioObjetivoId, pregunta.id, { respuestaTexto: e.target.value })
            }
            error={!!error}
            inputProps={{ maxLength: pregunta.limiteCaracteres }}
            helperText={
              pregunta.limiteCaracteres
                ? `${(answer.respuestaTexto || '').length}/${pregunta.limiteCaracteres}`
                : ''
            }
          />
        )}

        {(pregunta.tipo === 'seleccion_unica' || pregunta.tipo === 'test_seleccion_unica') &&
          (pregunta.opciones && pregunta.opciones.length > 0 ? (
            <RadioGroup
              value={answer.opcionId || ''}
              onChange={(e) =>
                handleAnswer(usuarioObjetivoId, pregunta.id, { opcionId: Number(e.target.value) })
              }
            >
              {pregunta.opciones.map((opcion) => (
                <FormControlLabel
                  key={opcion.id}
                  value={opcion.id}
                  control={<Radio />}
                  label={opcion.texto}
                />
              ))}
            </RadioGroup>
          ) : (
            <Alert severity="warning">Las opciones de esta pregunta aún se están cargando...</Alert>
          ))}

        {(pregunta.tipo === 'seleccion_multiple' || pregunta.tipo === 'test_seleccion_multiple') &&
          (pregunta.opciones && pregunta.opciones.length > 0 ? (
            <FormGroup>
              {pregunta.opciones.map((opcion) => (
                <FormControlLabel
                  key={opcion.id}
                  control={
                    <Checkbox
                      checked={(answer.opcionIds || []).includes(opcion.id)}
                      onChange={(e) => {
                        const current = answer.opcionIds || [];
                        const newIds = e.target.checked
                          ? [...current, opcion.id]
                          : current.filter((id: number) => id !== opcion.id);
                        handleAnswer(usuarioObjetivoId, pregunta.id, { opcionIds: newIds });
                      }}
                    />
                  }
                  label={opcion.texto}
                />
              ))}
            </FormGroup>
          ) : (
            <Alert severity="warning">Las opciones de esta pregunta aún se están cargando...</Alert>
          ))}

        {(pregunta.tipo === 'fecha' || pregunta.tipo === 'hora') && (
          <Typography variant="body2" color="text.secondary">
            {pregunta.tipo === 'fecha'
              ? `Fecha: ${new Date().toLocaleDateString('es-ES')}`
              : `Hora: ${new Date().toLocaleTimeString('es-ES')}`}
          </Typography>
        )}
      </Box>
    );
  };

  const usuariosProgreso =
    formulario.tipoFormulario && formulario.tipoFormulario !== 'general'
      ? usuariosSeleccionados
      : [0];

  const preguntaMap = new Map<number, FormularioPregunta>(
    preguntasConOpciones.map((p) => [p.id, p]),
  );

  const totalRespuestas = usuariosProgreso.reduce((acc, usuarioId) => {
    const visibles = visibleQuestionsByUser[usuarioId] || new Set<number>();
    return acc + visibles.size;
  }, 0);

  const respuestasCompletadas = usuariosProgreso.reduce((acc, usuarioId) => {
    const visibles = visibleQuestionsByUser[usuarioId] || new Set<number>();
    const respuestasUsuario = respuestasPorUsuario[usuarioId] || {};
    let count = 0;
    visibles.forEach((preguntaId) => {
      const pregunta = preguntaMap.get(preguntaId);
      if (!pregunta) return;
      if (pregunta.tipo === 'fecha' || pregunta.tipo === 'hora') {
        count += 1;
        return;
      }
      if (respuestasUsuario[preguntaId]) {
        count += 1;
      }
    });
    return acc + count;
  }, 0);

  const progress = totalRespuestas > 0 ? (respuestasCompletadas / totalRespuestas) * 100 : 0;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{formulario.titulo}</DialogTitle>
        <DialogContent>
          {resultadoEvaluacion ? (
            <Box py={2}>
              <Alert severity={resultadoEvaluacion.aprobado ? 'success' : 'warning'}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Nota: {resultadoEvaluacion.calificacion}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {resultadoEvaluacion.aprobado ? 'Aprobado' : 'No aprobado'}
                </Typography>
                {resultadoEvaluacion.competenciasFalladas &&
                  resultadoEvaluacion.competenciasFalladas.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Áreas de mejora:
                      </Typography>
                      <Typography variant="body2">
                        {resultadoEvaluacion.competenciasFalladas.join(', ')}
                      </Typography>
                    </Box>
                  )}
              </Alert>
            </Box>
          ) : (
            <>
              {formulario.descripcion && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {formulario.descripcion}
                </Typography>
              )}

              {formulario.respuestasAnonimas && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Las respuestas de este formulario son anónimas
                </Alert>
              )}

              {formulario.tipoFormulario &&
                formulario.tipoFormulario !== 'general' &&
                formulario.tipoFormulario !== 'evaluacion_managers' && (
                  <Box sx={{ mb: 3 }}>
                    {loadingUsuarios ? (
                      <CircularProgress size={24} />
                    ) : usuariosObjetivo.length === 0 ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        No hay usuarios disponibles en tu departamento para evaluar. Asegúrate de
                        que haya usuarios staff (no managers) en tu departamento.
                      </Alert>
                    ) : (
                      <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Todos los usuarios disponibles están seleccionados automáticamente. Es
                          obligatorio completar el formulario para todos ellos.
                        </Alert>
                        <Autocomplete
                          multiple
                          disabled
                          options={usuariosObjetivo}
                          getOptionLabel={(option) =>
                            option.nombreCompleto || `Usuario ${option.id}`
                          }
                          value={usuariosObjetivo.filter((u) =>
                            usuariosSeleccionados.includes(u.id),
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Usuarios objetivo (todos seleccionados)"
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const estaCompletado = usuariosCompletados.has(option.id);
                              return (
                                <Chip
                                  {...getTagProps({ index })}
                                  key={option.id}
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <span>{option.nombreCompleto || `Usuario ${option.id}`}</span>
                                      {estaCompletado && (
                                        <CheckCircleIcon
                                          sx={{ color: 'success.main', fontSize: '1rem' }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  color={estaCompletado ? 'success' : 'default'}
                                  variant={estaCompletado ? 'outlined' : 'filled'}
                                />
                              );
                            })
                          }
                          noOptionsText="No hay usuarios disponibles"
                        />
                      </>
                    )}
                  </Box>
                )}

              {formulario.tipoFormulario === 'evaluacion_managers' &&
                usuariosObjetivo.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Evaluando a: <strong>{usuariosObjetivo[0].nombreCompleto}</strong>
                    {formulario.respuestasAnonimas && ' (Respuesta anónima)'}
                  </Alert>
                )}

              <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />

              {/* Mejorado el indicador de borrador guardado */}
              {savingDraft ? (
                <Alert severity="info" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Guardando borrador...
                </Alert>
              ) : Object.values(draftSaved).some((saved) => saved) ? (
                <Alert severity="success" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: '1rem' }} />
                  Borrador guardado automáticamente
                </Alert>
              ) : null}

              {loadingFormulario ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : errorCargarFormulario ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorCargarFormulario}
                </Alert>
              ) : usuariosSeleccionados.length > 0 ? (
                <Stack spacing={4}>
                  {usuariosSeleccionados.map((usuarioObjetivoId) => {
                    const usuario = usuariosObjetivo.find((u) => u.id === usuarioObjetivoId);
                    return (
                      <Paper key={usuarioObjetivoId} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {usuario?.nombreCompleto || `Usuario #${usuarioObjetivoId}`}
                        </Typography>
                        <Stack spacing={2}>
                          {preguntasConOpciones
                            .sort(
                              (a: FormularioPregunta, b: FormularioPregunta) => a.orden - b.orden,
                            )
                            .map((p: FormularioPregunta) => renderQuestion(p, usuarioObjetivoId))}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : formulario.tipoFormulario === 'general' || !formulario.tipoFormulario ? (
                <Stack spacing={2}>
                  {preguntasConOpciones
                    .sort((a: FormularioPregunta, b: FormularioPregunta) => a.orden - b.orden)
                    .map((p: FormularioPregunta) => renderQuestion(p, 0))}
                </Stack>
              ) : (
                <Alert severity="warning">
                  {formulario.tipoFormulario === 'evaluacion_managers'
                    ? 'No hay manager disponible para evaluar'
                    : 'Selecciona al menos un usuario objetivo'}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {resultadoEvaluacion ? (
            <Button onClick={handleCerrarResultado} variant="contained">
              Cerrar
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={submitting}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={
                  submitting ||
                  !!(
                    formulario.tipoFormulario &&
                    formulario.tipoFormulario !== 'general' &&
                    usuariosSeleccionados.length === 0
                  )
                }
              >
                {submitting ? 'Enviando...' : 'Enviar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmExitFormularioDialog
        open={showConfirmExit}
        onClose={() => {
          setShowConfirmExit(false);
          setPendingClose(false);
        }}
        onDiscard={handleDiscardAndClose}
        onSaveDraft={handleSaveDraftAndClose}
        loading={savingDraft}
      />

      <ConfirmExitEvaluacionDialog
        open={showConfirmAutoSubmit}
        onClose={() => setShowConfirmAutoSubmit(false)}
        onContinue={handleSubmitPartial}
        loading={submitting}
      />
    </>
  );
}
